import {
  AdminPanelSettings,
  KeyboardArrowDown,
  LocalShipping,
  Logout,
  ManageAccounts,
  PriceCheck,
  SimCardDownload,
  UploadFile,
} from '@mui/icons-material';
import { Button, Menu, MenuItem, Stack } from '@mui/material';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ROUTES } from 'routing';
import { USER_GUIDE_PDF } from 'shared/constants';
import { useMenu } from 'shared/hooks';
import { useDownloadPdfFiles } from 'shared/mutations/use-download-pdf-files';
import { UserInfo } from 'shared/types';
import { determineIsAdmin } from 'shared/utility';

interface UserMenuProps {
  user: UserInfo;
  logout(): void;
}
const HEADER_MENU_BUTTON = `header-menu-button`;
const HEADER_MENU_NAME = `header-menu`;

export function UserMenu({ user, logout }: UserMenuProps) {
  const [anchorEl, openMenu, closeMenu, open] = useMenu();
  const productsMatch = useRouteMatch({ path: ROUTES.products });
  const customerCommoditiesMatch = useRouteMatch({ path: ROUTES.manageCustomerParameters });
  const freightMatch = useRouteMatch({ path: ROUTES.freight });
  const pdfUploadMatch = useRouteMatch({ path: ROUTES.uploadPDF });
  const pdfDownloadMutation = useDownloadPdfFiles();

  const history = useHistory();

  const isAdmin = determineIsAdmin(user);
  const isInsightEmployee = user?.isInsightEmployee;

  interface UserMenuItem {
    condition: boolean;
    onClick(): void | Promise<void>;
    name: string;
    icon: JSX.Element;
  }

  const menuItems: UserMenuItem[] = [
    {
      condition: !Boolean(productsMatch),
      icon: <PriceCheck />,
      name: 'Best Price Review',
      onClick: () => {
        history.push(ROUTES.products);
      },
    },
    {
      condition: !Boolean(customerCommoditiesMatch),
      icon: <ManageAccounts />,
      name: 'Customer Parameter Management',
      onClick: () => {
        history.push(ROUTES.manageCustomerParameters);
      },
    },
    {
      condition: !Boolean(freightMatch),
      icon: <LocalShipping />,
      name: 'Freight Management',
      onClick: () => {
        history.push(ROUTES.freight);
      },
    },
    {
      condition: isAdmin && (Boolean(productsMatch) || Boolean(pdfUploadMatch) || Boolean(freightMatch)),
      icon: <AdminPanelSettings />,
      name: 'User Mapping',
      onClick: () => {
        history.push(ROUTES.userMapping);
      },
    },
    {
      condition: true,
      icon: <SimCardDownload />,
      name: 'Download User Guide',
      onClick: async () => {
        await pdfDownloadMutation.mutateAsync({
          fileId: USER_GUIDE_PDF,
        });
      },
    },
    {
      condition: isInsightEmployee,
      icon: <UploadFile />,
      name: 'Upload User Guide',
      onClick: () => {
        history.push(ROUTES.uploadPDF);
      },
    },
    {
      condition: true,
      icon: <Logout />,
      name: 'Logout',
      onClick: logout,
    },
  ];

  return (
    <>
      <Menu
        id={HEADER_MENU_NAME}
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        MenuListProps={{
          'aria-labelledby': HEADER_MENU_BUTTON,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        {menuItems.map(
          item =>
            item.condition && (
              <MenuItem
                key={item.name}
                onClick={async () => {
                  await item.onClick();
                  closeMenu();
                }}
              >
                <Stack alignItems={'center'} direction={'row'} gap={1}>
                  {item.icon}
                  {item.name}
                </Stack>
              </MenuItem>
            )
        )}
      </Menu>
      <Button
        size='large'
        id={HEADER_MENU_BUTTON}
        aria-controls={open ? HEADER_MENU_NAME : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={openMenu}
        sx={{
          justifySelf: 'end',
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          color: 'primary.contrastText',
        }}
      >
        {user.displayName} <KeyboardArrowDown />
      </Button>
    </>
  );
}
